import React, { useState, useEffect } from 'react';
import { Calculator, Plus, Trash2, ShoppingCart } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductCalculator = ({ token, onProductsUpdate, embedded = false }) => {
  const [listini, setListini] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedListino, setSelectedListino] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedPrice, setSelectedPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [cart, setCart] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Debug: mostra token
  useEffect(() => {
    console.log('ProductCalculator mounted with token:', token ? 'Present' : 'Missing');
    console.log('Backend URL:', BACKEND_URL);
  }, []);

  // Carica listini al mount
  useEffect(() => {
    if (token) {
      fetchListini();
      fetchProducts();
    } else {
      console.error('No token available for API calls');
      setError('Token di autenticazione mancante');
    }
  }, [token]);

  // Filtra prodotti quando cambia il listino
  useEffect(() => {
    if (selectedListino && products.length > 0) {
      const filtered = products.filter(p => p.listino === selectedListino);
      setFilteredProducts(filtered);
      setSelectedProduct(null);
      setSelectedPrice('');
    } else {
      setFilteredProducts([]);
    }
  }, [selectedListino, products]);

  // Calcola totali quando cambia il carrello
  useEffect(() => {
    if (cart.length > 0) {
      calculateTotals();
    } else {
      setTotals(null);
      // Notify parent component
      if (onProductsUpdate) {
        onProductsUpdate([], null);
      }
    }
  }, [cart]);

  const fetchListini = async () => {
    try {
      console.log('Fetching listini from:', `${BACKEND_URL}/api/crm/listini`);
      const response = await fetch(`${BACKEND_URL}/api/crm/listini`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Listini fetched:', data);
        setListini(data);
      } else {
        console.error('Failed to fetch listini:', response.status);
      }
    } catch (err) {
      console.error('Error fetching listini:', err);
      setError('Errore nel caricamento dei listini');
    }
  };

  const fetchProducts = async () => {
    try {
      console.log('Fetching products from:', `${BACKEND_URL}/api/crm/products`);
      const response = await fetch(`${BACKEND_URL}/api/crm/products`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        console.log('Products fetched:', data.length);
        setProducts(data);
      } else {
        console.error('Failed to fetch products:', response.status);
      }
    } catch (err) {
      console.error('Error fetching products:', err);
      setError('Errore nel caricamento dei prodotti');
    }
  };

  const handleProductChange = (productId) => {
    const product = filteredProducts.find(p => p.id === productId);
    setSelectedProduct(product);
    setSelectedPrice('');
  };

  const addToCart = () => {
    // Validazione diversa per prodotti con prezzo vs terminali
    const isTerminale = selectedProduct?.prezzi_disponibili.length === 0;
    
    if (!selectedProduct || quantity < 1) {
      setError('Seleziona un prodotto e inserisci quantità');
      return;
    }
    
    if (!isTerminale && !selectedPrice) {
      setError('Seleziona un prezzo per questo prodotto');
      return;
    }

    const newItem = {
      product_id: selectedProduct.id,
      listino: selectedProduct.listino,
      nome_prodotto: selectedProduct.nome_prodotto,
      prezzo_vendita: isTerminale ? 0 : parseFloat(selectedPrice),
      quantita: parseInt(quantity),
      mol: selectedProduct.mol || 0
    };

    setCart([...cart, newItem]);
    setError('');
    
    // Reset form
    setSelectedProduct(null);
    setSelectedPrice('');
    setQuantity(1);
  };

  const removeFromCart = (index) => {
    setCart(cart.filter((_, i) => i !== index));
  };

  const calculateTotals = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/crm/calcola_totali`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ prodotti_venduti: cart })
      });

      if (response.ok) {
        const data = await response.json();
        setTotals(data);
        // Notify parent component
        if (onProductsUpdate) {
          onProductsUpdate(cart, data);
        }
      }
    } catch (err) {
      console.error('Error calculating totals:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800 rounded-xl p-6 border border-gray-700">
      <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
        <Calculator className="w-7 h-7 mr-3 text-green-400" />
        Calcolatore Prodotti & INFLOW
      </h2>

      {/* Form Selezione Prodotto */}
      <div className="space-y-4 mb-6">
        {/* Listino */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            1. Seleziona Listino
          </label>
          <select
            value={selectedListino}
            onChange={(e) => setSelectedListino(e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-green-500"
          >
            <option value="">-- Scegli Listino --</option>
            {listini.map(listino => (
              <option key={listino} value={listino}>{listino}</option>
            ))}
          </select>
        </div>

        {/* Prodotto */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            2. Seleziona Prodotto
          </label>
          <select
            value={selectedProduct?.id || ''}
            onChange={(e) => handleProductChange(e.target.value)}
            disabled={!selectedListino}
            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-green-500 disabled:opacity-50"
          >
            <option value="">-- Scegli Prodotto --</option>
            {filteredProducts.map(product => (
              <option key={product.id} value={product.id}>
                {product.nome_prodotto}{product.mol ? ` (MOL: ${product.mol})` : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Prezzo - Solo se il prodotto ha prezzi disponibili */}
        {selectedProduct && selectedProduct.prezzi_disponibili.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              3. Seleziona Prezzo Vendita
            </label>
            <select
              value={selectedPrice}
              onChange={(e) => setSelectedPrice(e.target.value)}
              className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-green-500"
            >
              <option value="">-- Scegli Prezzo --</option>
              {selectedProduct.prezzi_disponibili.map((price, idx) => (
                <option key={idx} value={price}>€ {price.toFixed(2)}</option>
              ))}
            </select>
          </div>
        )}
        
        {/* Messaggio per terminali (senza prezzo) */}
        {selectedProduct && selectedProduct.prezzi_disponibili.length === 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3">
            <p className="text-xs text-blue-300">
              ℹ️ Questo è un terminale - non richiede prezzo, solo quantità
            </p>
          </div>
        )}

        {/* Quantità */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            {selectedProduct?.prezzi_disponibili.length > 0 ? '4. Quantità' : '3. Quantità'}
          </label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full px-4 py-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-green-500"
          />
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-3">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        {/* Bottone Aggiungi */}
        <button
          onClick={addToCart}
          className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2"
        >
          <Plus size={20} />
          <span>Aggiungi al Carrello</span>
        </button>
      </div>

      {/* Carrello */}
      {cart.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3 flex items-center">
            <ShoppingCart className="w-5 h-5 mr-2 text-blue-400" />
            Carrello Prodotti ({cart.length})
          </h3>
          <div className="space-y-2">
            {cart.map((item, index) => (
              <div key={index} className="bg-gray-700 p-4 rounded-lg flex items-center justify-between">
                <div className="flex-grow">
                  <p className="text-white font-medium">{item.listino} - {item.nome_prodotto}</p>
                  <p className="text-sm text-gray-400">
                    {item.prezzo_vendita > 0 
                      ? `€ ${item.prezzo_vendita.toFixed(2)} × ${item.quantita}${item.mol ? ` (MOL: ${item.mol})` : ''}`
                      : `Quantità: ${item.quantita} (Terminale)`
                    }
                  </p>
                </div>
                <button
                  onClick={() => removeFromCart(index)}
                  className="ml-4 p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Totali */}
      {totals && (
        <div className="bg-gradient-to-r from-green-600/20 to-blue-600/20 border border-green-500/50 rounded-xl p-6">
          <h3 className="text-xl font-bold text-white mb-4">📊 Riepilogo Calcoli</h3>
          
          {/* Dettaglio Prodotti */}
          <div className="space-y-3 mb-4">
            {totals.prodotti.map((prod, idx) => (
              <div key={idx} className="bg-gray-800/50 p-3 rounded-lg">
                <p className="text-white font-medium mb-1">
                  {prod.nome_prodotto} ({prod.listino})
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-gray-400">
                    INFLOW: <span className="text-green-400 font-semibold">€ {prod.inflow_singolo.toFixed(2)}</span>
                  </p>
                  <p className="text-gray-400">
                    Pagamento Agente: <span className="text-blue-400 font-semibold">€ {prod.pagamento_agente_singolo.toFixed(2)}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Totali Finali */}
          <div className="border-t border-gray-600 pt-4 grid grid-cols-2 gap-4">
            <div className="bg-green-500/20 p-4 rounded-lg">
              <p className="text-sm text-gray-300 mb-1">INFLOW TOTALE</p>
              <p className="text-3xl font-bold text-green-400">€ {totals.inflow_totale.toFixed(2)}</p>
            </div>
            <div className="bg-blue-500/20 p-4 rounded-lg">
              <p className="text-sm text-gray-300 mb-1">PAGAMENTO AGENTE</p>
              <p className="text-3xl font-bold text-blue-400">€ {totals.pagamento_agente_totale.toFixed(2)}</p>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="text-center py-4">
          <p className="text-gray-400">Calcolo in corso...</p>
        </div>
      )}
    </div>
  );
};

export default ProductCalculator;
